import { useKeycloak } from '@react-keycloak/web';
import { FC, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { Box, Popover, Typography } from '@mui/material';

import { useQuery } from '@tanstack/react-query';

import { UserInfo } from '@/api/data-contracts';
import { ROUTES } from '@/config/routes';
import { useUsersService } from '@/features/users';
import { queryKeys } from '@/react-query/constants';
// import { useGuideContext } from '../Guide/GuideContext';
import { UserTypeContext } from '@/shared/lib/UserTypeContext/UserTypeContext';
import useMixpanel from '@/shared/lib/analytics/mixpanel.service';
import { useClearQueryCache } from '@/shared/lib/hooks/useClearQueryCache';
import { resetNavigationHistory } from '@/shared/lib/hooks/useNavigationHistory';
import useTableStore from '@/shared/model/tableStore';

import AccountButton from './AccountButton';
import {
  AccountMenuDivider,
  AccountMenuItem,
  AccountMenuItemText,
  AccountMenuItemsContainer,
  AccountMenuSeparator,
  AccountMenuWrapper,
} from './styled';

interface KeycloakUserInfo {
  name?: string | undefined;
  organization_types?: string | undefined;
}

interface Props {
  isExpanded: boolean;
}

const AccountMenu: FC<Props> = ({ isExpanded }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const userType = useContext(UserTypeContext);
  const { keycloak, initialized } = useKeycloak();
  const { logout } = useMixpanel();
  const { t } = useTranslation();
  const { clearAll } = useTableStore();
  const clearQueryCache = useClearQueryCache();
  // const { guideSettings, startGuide, resetGuide } = useGuideContext();
  const { getCurrentUserDetails } = useUsersService();

  const [isMenuExpanded, setIsMenuExpanded] = useState<boolean>(false);
  const [userInfo, setUserInfo] = useState<KeycloakUserInfo>();
  const [hoverAnchorEl, setHoverAnchorEl] = useState<HTMLElement | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    if (isExpanded) return;
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setHoverAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = () => {
    closeTimerRef.current = setTimeout(() => setHoverAnchorEl(null), 200);
  };

  useEffect(() => {
    setHoverAnchorEl(null);
  }, [location.pathname]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (initialized && keycloak?.authenticated && !userInfo) {
      keycloak.loadUserInfo().then((data: KeycloakUserInfo) => {
        setUserInfo(data);
      });
    }
  }, [initialized, keycloak, userInfo]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuExpanded(false);
      }
    };

    if (isMenuExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuExpanded]);

  const { data: currentUser } = useQuery<UserInfo>({
    queryKey: [queryKeys.currentUser],
    queryFn: ({ signal }) => getCurrentUserDetails(signal),
  });

  // useEffect(() => {
  //   if (userType === 'advertiser' && !guideSettings?.onboarding?.completed) {
  //     navigate(ROUTES.AUTHORIZED.ONBOARDING);
  //   }
  // }, [guideSettings?.onboarding?.completed, navigate, userType]);

  const handleLogout = () => {
    clearAll();
    resetNavigationHistory();
    logout();
    clearQueryCache();
    keycloak.logout({
      redirectUri: window.location.href,
    });
  };

  const menuItems = [
    {
      label: t('buttons.logout'),
      onClick: handleLogout,
    },
    ...(userInfo?.organization_types
      ?.split(',')
      .filter((type) => type.replaceAll('SERVICE_PROVIDER', 'SERVICE').toLowerCase() !== userType)
      .map((type) => type.replace('SERVICE_PROVIDER', 'SERVICES'))
      .map((type) => ({
        label: t(`Switch to ${type.toLowerCase()} module`),
        onClick: () => {
          const url = window.location.origin.replace(
            /^(https?:\/\/)?([^./]+)/,
            (_, protocol = '') => {
              return `${protocol}${type.toLowerCase()}`;
            },
          );
          window.open(url, '_blank');
        },
      })) || []),
    // {
    //   label: t('Start onboarding'),
    //   onClick: () => navigate(ROUTES.AUTHORIZED.ONBOARDING),
    // },
    // ...(userType !== 'service'
    //   ? [
    //       {
    //         label: t(guideSettings?.guide?.step ? 'buttons.continue_tour' : 'buttons.start_tour'),
    //         onClick: startGuide,
    //       },
    //       ...(guideSettings?.guide?.step
    //         ? [
    //             {
    //               label: t('Reset Tour'),
    //               onClick: resetGuide,
    //             },
    //           ]
    //         : []),
    //     ]
    //   : []),
    {
      label: t('buttons.account_settings'),
      onClick: () => navigate(ROUTES.AUTHORIZED.USER_PROFILE),
    },
  ];

  return (
    <>
      <AccountMenuWrapper
        ref={menuRef}
        isExpanded={isMenuExpanded}
        sx={{ overflow: isMenuExpanded ? 'hidden' : 'visible' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {isMenuExpanded && (
          <>
            <AccountMenuItemsContainer isExpanded={isMenuExpanded}>
              {menuItems.map((item, index) => (
                <AccountMenuItem
                  key={index}
                  onClick={() => {
                    item.onClick();
                    setIsMenuExpanded(false);
                  }}
                  sx={{ width: '100%' }}
                  data-testid={`account-menu-item-${index}`}
                >
                  <AccountMenuItemText>{item.label}</AccountMenuItemText>
                </AccountMenuItem>
              ))}
            </AccountMenuItemsContainer>

            <AccountMenuSeparator>
              <AccountMenuDivider />
            </AccountMenuSeparator>
          </>
        )}

        <AccountButton
          isExpanded={isExpanded}
          userInfo={currentUser}
          setIsMenuExpanded={setIsMenuExpanded}
        />
      </AccountMenuWrapper>

      <Popover
        open={Boolean(hoverAnchorEl) && !isExpanded}
        anchorEl={hoverAnchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        disableRestoreFocus
        disableAutoFocus
        disableEnforceFocus
        sx={{ pointerEvents: 'none' }}
        PaperProps={{
          elevation: 0,
          sx: {
            transform: 'rotate(0.04deg)',
            borderRadius: '0.75rem',
            bgcolor: '#fefefe',
            boxShadow: '0px 1px 8px 0px rgba(0, 0, 0, 0.15)',
            ml: '1rem',
            mt: '0.5rem',
            pointerEvents: 'auto',
          },
          onMouseEnter: () => {
            if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
          },
          onMouseLeave: handleMouseLeave,
        }}
      >
        <Box
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', p: '0.5rem' }}
        >
          {menuItems.map((item, index) => (
            <Box
              key={index}
              onClick={() => {
                item.onClick();
                setHoverAnchorEl(null);
              }}
              data-testid={`account-hover-item-${index}`}
              sx={{
                width: '100%',
                cursor: 'pointer',
                px: '0.5rem',
                py: '0.25rem',
                borderRadius: '0.5rem',
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              <Typography
                sx={{
                  color: 'grey.800',
                  fontSize: '0.8125rem',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: 1.6,
                  whiteSpace: 'nowrap',
                }}
              >
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
};

export default AccountMenu;
